/**
 * useVersionCheck — polls /version.json periodically and compares against
 * the build-time version baked into the running bundle.
 *
 * When a mismatch is detected, sets `updateAvailable` to true so the UI
 * can show a non-intrusive reload banner.
 *
 * Polling interval: 5 minutes (only when tab is visible).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const BUILD_VERSION = process.env.APP_VERSION ?? '0.0.0';

export function useVersionCheck(): { updateAvailable: boolean; currentVersion: string; dismiss: () => void } {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch('/version.json', { cache: 'no-store' });
      if (!response.ok) return;
      const data = await response.json();
      if (data.version && data.version !== BUILD_VERSION) {
        setUpdateAvailable(true);
      }
    } catch {
      // Network error — silently ignore, will retry on next interval
    }
  }, []);

  useEffect(() => {
    // Initial check after a short delay (don't block page load)
    const initialTimeout = setTimeout(checkVersion, 10_000);

    // Set up periodic polling
    timerRef.current = setInterval(checkVersion, POLL_INTERVAL_MS);

    // Pause polling when tab is hidden, resume when visible
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(initialTimeout);
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkVersion]);

  const dismiss = useCallback(() => setDismissed(true), []);

  return {
    updateAvailable: updateAvailable && !dismissed,
    currentVersion: BUILD_VERSION,
    dismiss,
  };
}
